import React, { useCallback, useEffect, useState } from "react";
import { FaBell, FaCheck, FaInfoCircle } from "react-icons/fa";
import { fetchFarmOperators } from "../Radio/radioApi";
import { fetchOperatorAlerts, sendOperatorAlert } from "./fieldAppApi";
import { OperatorAlertsHelpContent } from "./operatorAlertsHelpContent";

function formatWhen(value, t) {
  if (!value) return t("Ainda não leu");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("Ainda não leu");
  return date.toLocaleString("pt-BR");
}

function splitRecipients(recipients) {
  const read = [];
  const unread = [];
  (recipients || []).forEach((item) => {
    if (item.ackedAt) {
      read.push(item);
    } else {
      unread.push(item);
    }
  });
  return { read, unread };
}

export default function FieldAppOperatorAlertsPanel({ farmId, t }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [operators, setOperators] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sendAll, setSendAll] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [operatorList, alertsData] = await Promise.all([
        fetchFarmOperators(farmId),
        fetchOperatorAlerts(farmId),
      ]);
      setOperators(operatorList || []);
      setAlerts((alertsData && alertsData.results) || []);
    } catch (loadError) {
      setError(t("Não foi possível carregar os alertas."));
    } finally {
      setLoading(false);
    }
  }, [farmId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOperator = (id) => {
    setSendAll(false);
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : current.concat(id)
    );
  };

  const handleSendAllChange = (checked) => {
    setSendAll(checked);
    if (checked) {
      setSelectedIds(operators.map((operator) => operator.id));
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!title.trim() || !body.trim()) {
      setError(t("Informe título e mensagem."));
      return;
    }
    if (!sendAll && !selectedIds.length) {
      setError(t("Escolha ao menos um operador, ou envie para todos."));
      return;
    }
    setSending(true);
    setError(null);
    try {
      await sendOperatorAlert(farmId, {
        title: title.trim(),
        body: body.trim(),
        operatorIds: sendAll ? [] : selectedIds,
      });
      setTitle("");
      setBody("");
      await load();
    } catch (sendError) {
      setError(t("Não foi possível enviar o alerta."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="field-app-calibration">
      <div className="field-app-calibration-help" role="note">
        <FaInfoCircle className="field-app-calibration-help-icon" aria-hidden />
        <div className="field-app-calibration-help-body">
          <OperatorAlertsHelpContent />
        </div>
      </div>

      <form className="field-app-operator-alert-form" onSubmit={handleSend}>
        <h3>
          <FaBell aria-hidden /> {t("Enviar alerta ao app")}
        </h3>
        <label>
          {t("Título")}
          <input
            className="form-control"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={160}
          />
        </label>
        <label>
          {t("Mensagem")}
          <textarea
            className="form-control"
            rows={5}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        </label>
        <fieldset className="field-app-operator-alert-recipients">
          <legend>{t("Enviar para")}</legend>
          <label className="field-app-operator-alert-all">
            <input
              type="checkbox"
              checked={sendAll}
              onChange={(event) => handleSendAllChange(event.target.checked)}
            />
            {t("Todos os operadores da fazenda")}
          </label>
          <div className="field-app-operator-alert-ops">
            {operators.map((operator) => (
              <label key={operator.id}>
                <input
                  type="checkbox"
                  checked={sendAll || selectedIds.includes(operator.id)}
                  onChange={() => toggleOperator(operator.id)}
                />
                {operator.name}
              </label>
            ))}
          </div>
          {!operators.length && !loading ? (
            <p className="field-app-operator-alert-empty-ops">
              {t("Nenhum operador cadastrado nesta fazenda.")}
            </p>
          ) : null}
        </fieldset>
        {error ? <p className="field-app-activity-session-error">{error}</p> : null}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={sending}
        >
          {sending ? t("Enviando...") : t("Enviar alerta")}
        </button>
      </form>

      <section className="field-app-operator-alert-history">
        <h3>{t("Histórico de alertas")}</h3>
        <p className="field-app-operator-alert-history-hint">
          {t(
            "Alertas e notificações enviados ao app, com quem confirmou a leitura e quem ainda não leu."
          )}
        </p>
        {loading ? <p>{t("Carregando...")}</p> : null}
        {!loading && !alerts.length ? (
          <p>{t("Nenhum alerta enviado ainda.")}</p>
        ) : null}
        {alerts.map((alert) => {
          const { read, unread } = splitRecipients(alert.recipients);
          return (
            <article key={alert.uuid} className="field-app-operator-alert-card">
              <header>
                <strong>{alert.title}</strong>
                <span>
                  {alert.ackCount}/{alert.recipientCount} {t("confirmaram a leitura")}
                </span>
              </header>
              {alert.createdAt ? (
                <p className="field-app-operator-alert-sent-at">
                  {t("Enviado em")} {formatWhen(alert.createdAt, t)}
                </p>
              ) : null}
              <p>{alert.body}</p>
              <div className="field-app-operator-alert-receipts">
                <div>
                  <h4>{t("Confirmaram a leitura")}</h4>
                  {read.length ? (
                    <ul className="field-app-operator-alert-read-list">
                      {read.map((item) => (
                        <li key={item.operatorId} className="field-app-operator-alert-read">
                          <FaCheck
                            className="field-app-operator-alert-read-icon"
                            aria-hidden
                          />
                          <span className="field-app-operator-alert-read-body">
                            <span className="field-app-operator-alert-read-line">
                              <span className="field-app-operator-alert-read-name">
                                {item.operatorName}
                              </span>
                              <span className="field-app-operator-alert-read-mark">
                                — OK
                              </span>
                              <span className="field-app-operator-alert-read-leu">
                                · {t("Leu")}
                              </span>
                            </span>
                            <span className="field-app-operator-alert-read-when">
                              {formatWhen(item.ackedAt, t)}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>{t("Ninguém confirmou ainda.")}</p>
                  )}
                </div>
                <div>
                  <h4>{t("Ainda não leram")}</h4>
                  {unread.length ? (
                    <ul>
                      {unread.map((item) => (
                        <li key={item.operatorId}>{item.operatorName}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{t("Todos confirmaram.")}</p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
